import { useEffect, useState, useLayoutEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  FaMicrophone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaYoutube,
} from 'react-icons/fa'
import { richTextSummaryHandler } from '../components/content/RichTextSummary/RichTextSummary'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { HERO_ART } from '../config/heroArt'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

export default function TalksPage({ items: propItems = [] }: { items?: any[] }) {
  const talks = useMemo(() => {
    const items = propItems
    return items.map((item: any) => ({
      ...item,
      createdAt: item.createdAt || item.created_at,
      updatedAt: item.updatedAt || item.updated_at,
      publishedAt: item.publishedAt || item.published_at,
      content: item.content_md || item.content || '',
    }))
  }, [])

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }
    measure()
    requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const totalTalks = talks.length

  return (
    <div className="min-h-screen bg-gray-50 text-[#232323]">

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="talks-hero" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <span className="mb-8 inline-flex w-fit items-center gap-2 font-body text-[15px] text-gray-50/80 leading-[1.58] tracking-[-0.003em]">
              <FaMicrophone className="text-base" aria-hidden="true" />
              {totalTalks} Talks & Presentations Available
            </span>

            <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
              Things I've <InlineHighlight color="red">talked about</InlineHighlight>
            </h1>

            <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body tracking-[-0.003em]">
              Conference talks, meetup presentations, and workshops on system design, engineering leadership, and the lessons I've picked up shipping software for 16+ years.
            </p>
          </div>
          <HeroArtGrid images={HERO_ART.talks} />
          </div>
        </ContentColumn>
      </section>

      {/* Breadcrumb Section */}
      <section className="border-b border-border-subtle bg-gray-50">
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em] text-gray-900/60 sm:text-sm">
              <li>
                <Link
                  to="/"
                  className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                >
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Talks
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Talks Grid Section */}
      <section id="talks-list" className="scroll-mt-20 bg-white py-16">
        <ContentColumn anchor="talks-list" className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <h2 className="mb-4 font-heading text-3xl font-bold uppercase text-gray-900">
              All <InlineHighlight color="navy">talks</InlineHighlight>
            </h2>
            <p className="font-body text-sm text-ink-soft sm:text-base">
              Every conference talk, workshop, and presentation I've given -- from small meetups to large stages.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {talks.map((talk: any) => {
              const talkDate = talk.createdAt
                ? new Date(talk.createdAt).toLocaleDateString('en-US', {
                    timeZone: 'UTC',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : null

              const isNew =
                talk.createdAt &&
                new Date().getTime() - new Date(talk.createdAt).getTime() <
                  1000 * 60 * 60 * 24 * 30

              const hasVideo = talk.youtubeUrl || talk.youtubeId
              const talkUrl = talk.slug ? `/talks/${talk.slug}` : null

              const renderDescription = () => {
                if (!talk.content) return null
                const summary =
                  typeof talk.content === 'string'
                    ? talk.content.substring(0, 150) + (talk.content.length > 150 ? '...' : '')
                    : (richTextSummaryHandler({ content: talk.content }) || '').substring(0, 150) +
                      '...'
                return <p>{summary}</p>
              }

              const cardContent = (
                <>
                  {(talk.image?.url || talk.image?.formats?.small?.url) && (
                    <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
                      <div className="h-full w-full overflow-hidden">
                        <img
                          src={talk.image?.formats?.small?.url || talk.image?.url}
                          alt={talk.title || 'Talk'}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                      </div>
                      {isNew && (
                        <span className="absolute right-3 top-3 bg-gray-900 px-2 py-1 font-heading text-xs font-bold uppercase text-white">
                          New
                        </span>
                      )}
                      {hasVideo && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/0 transition-colors duration-300 group-hover:bg-gray-900/10">
                          <div className="rounded-full bg-gray-900/80 p-4 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <FaYoutube className="text-2xl" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="mb-3 line-clamp-2 font-heading text-lg font-bold uppercase leading-tight text-gray-900 transition-colors duration-200 group-hover:text-[#232323]">
                      {talk.title}
                    </h3>
                    <div className="mb-4 space-y-2">
                      {talkDate && (
                        <div className="flex items-center gap-2 font-body text-sm text-gray-900/70">
                          <FaCalendarAlt className="flex-shrink-0 text-xs" />
                          <span>{talkDate}</span>
                        </div>
                      )}
                      {talk.location && (
                        <div className="flex items-center gap-2 font-body text-sm text-gray-900/70">
                          <FaMapMarkerAlt className="flex-shrink-0 text-xs" />
                          <span>{talk.location}</span>
                        </div>
                      )}
                    </div>
                    {talk.content && (
                      <div className="mb-4 flex-1 font-body text-sm leading-relaxed text-gray-900/70 line-clamp-3">
                        {renderDescription()}
                      </div>
                    )}
                    <div className="mt-auto border-t border-gray-200 pt-4">
                      <div className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-gray-900 transition-colors duration-200 group-hover:text-[#232323]">
                        {hasVideo ? (
                          <>
                            <span>Watch on YouTube</span>
                            <FaExternalLinkAlt className="text-xs" />
                          </>
                        ) : (
                          <>
                            <span>Read More</span>
                            <svg
                              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={2.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )

              const cardClass =
                'block h-full overflow-hidden rounded-smooth-lg border-2 border-gray-200 bg-brand-cream transition-all duration-300 hover:border-gray-300 hover:shadow-xl group'

              if (hasVideo) {
                return (
                  <a
                    key={talk.id}
                    href={talk.youtubeUrl || `https://www.youtube.com/watch?v=${talk.youtubeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cardClass}
                  >
                    <div className="flex h-full flex-col">{cardContent}</div>
                  </a>
                )
              }

              if (talkUrl) {
                return (
                  <Link key={talk.id} to={talkUrl} className={cardClass}>
                    <div className="flex h-full flex-col">{cardContent}</div>
                  </Link>
                )
              }

              return (
                <div key={talk.id} className={cardClass}>
                  <div className="flex h-full flex-col">{cardContent}</div>
                </div>
              )
            })}
          </div>

          {talks.length === 0 && (
            <div className="py-16 text-center">
              <FaMicrophone className="mx-auto mb-4 text-6xl text-gray-400" />
              <h3 className="mb-2 font-heading text-xl font-bold uppercase text-gray-900">
                No Talks Available
              </h3>
              <p className="font-body text-gray-900/70">
                Check back soon for upcoming talks and presentations.
              </p>
            </div>
          )}
        </ContentColumn>
      </section>
    </div>
  )
}
